import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { SeattleGameState, GoodsColor } from '../../types';
import { placeGoodsSeattle, triggerDemandOverload, resetGoodsPlacement } from '../moves/goodsGrowthSeattle';
import { SEATTLE_GRAY_CITIES, SEATTLE_RULES } from '../../../data/map/seattle_metro/config';

/**
 * Phase 9: 商品補充フェーズ（Seattle固有）
 *
 * Seattle では以下の特殊ルールが適用されます:
 * - 10ターン目はスキップ
 * - まず Airport に商品1個を自動配置
 * - ダイス3個を振り、対応する City/Town にプレイヤーが配置
 * - 需要過多（3個目配置）で即敗北
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_09_goods_growth_seattle.md
 */
export const goodsGrowthSeattlePhase: PhaseConfig<SeattleGameState> = {
  moves: {
    placeGoods: placeGoodsSeattle,
    triggerDemandOverload,
    resetGoodsPlacement,
    // ユーザークリック（Finish）でフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: SeattleGameState; events: any }) => {
      G.autoPhaseResult = null;
      events.endPhase();
    },
  },

  onBegin: ({ G }: { G: SeattleGameState }) => {
    debug.log(`[goodsGrowthSeattlePhase] Phase 9: Goods Growth started (Turn ${G.actualTurn})`);

    // フェーズ開始フラグを設定（endIfで使用）
    (G as any).refillGoodsStarted = true;

    // 10ターン目はスキップ
    if (G.actualTurn >= 10) {
      debug.log(`[goodsGrowthSeattlePhase] Turn 10 - skipping Phase 9`);
      G.autoPhaseResult = {
        phase: 'refillGoods',
        message: 'Final turn - skipping goods growth',
        floatingText: 'Skip',
        details: { skipped: true }
      };
      return;
    }

    // 1. Airport に商品1個を自動配置（hex.goods に直接 push）
    let airportCube: GoodsColor | undefined;
    const airportHex = G.map.find(h => h.city?.id === SEATTLE_GRAY_CITIES.AIRPORT);
    if (airportHex && G.cubesInBag.length > 0) {
      // 容量チェック（3個満杯なら需要過多）
      // ※ 即ゲームオーバーにせず、UI で Game Over ボタンを表示させる（Request #5）
      if ((airportHex.goods?.length || 0) >= SEATTLE_RULES.airportGoodsCapacity) {
        G.autoPhaseResult = {
          phase: 'refillGoods',
          message: 'Airport overloaded! Demand exceeded capacity.',
          floatingText: 'Demand Overload!',
          details: {
            airportOverload: true,  // UI で検出してゲームオーバーボタンを表示
            demandOverload: true,
            location: 'AIRPORT'
          }
        };
        debug.log(`[goodsGrowthSeattlePhase] Airport full (${airportHex.goods?.length} cubes) - waiting for user confirmation`);
        return;  // ダイス処理はスキップ
      }
      const randomIndex = Math.floor(Math.random() * G.cubesInBag.length);
      airportCube = G.cubesInBag.splice(randomIndex, 1)[0];
      if (!airportHex.goods) airportHex.goods = [];
      airportHex.goods.push(airportCube);
      debug.log(`[goodsGrowthSeattlePhase] Placed ${airportCube} at Airport hex.goods (auto)`);
    }

    // 2. ダイス3個を振る
    const dice1 = Math.floor(Math.random() * 6) + 1;
    const dice2 = Math.floor(Math.random() * 6) + 1;
    const dice3 = Math.floor(Math.random() * 6) + 1;
    G.lastDiceRolls = [dice1, dice2, dice3];
    debug.log(`[goodsGrowthSeattlePhase] Dice rolled: ${dice1}, ${dice2}, ${dice3}`);

    // 3. バッグから商品3個を引く
    const drawnCubes: GoodsColor[] = [];
    for (let i = 0; i < 3 && G.cubesInBag.length > 0; i++) {
      const randomIndex = Math.floor(Math.random() * G.cubesInBag.length);
      drawnCubes.push(G.cubesInBag.splice(randomIndex, 1)[0]);
    }

    // 4. 配置待ちデータを初期化（ダイスとキューブを分離）
    G.lastGoodsPlacement = [];
    G.pendingGoodsPlacement = {
      remainingDice: [dice1, dice2, dice3],
      remainingCubes: drawnCubes
    };

    debug.log(`[goodsGrowthSeattlePhase] Drawn cubes: ${drawnCubes.join(', ')}`);

    // 処理結果を保存（UI表示用）
    G.autoPhaseResult = {
      phase: 'refillGoods',
      message: 'Rolling dice for goods placement...',
      floatingText: `Dice: ${dice1}, ${dice2}, ${dice3}`,
      details: {
        diceRolls: [dice1, dice2, dice3],
        drawnCubes,
        airportCube
      }
    };
  },

  endIf: ({ G }: { G: SeattleGameState }) => {
    // フェーズ開始前は終了しない（Issue #15対策）
    const started = (G as any).refillGoodsStarted === true;
    if (!started) {
      return false;
    }

    // ゲームオーバー（需要過多）
    if (G.isGameOver) {
      return true;
    }

    // 10ターン目スキップ、および3個配置完了後は proceedAutoPhase を待つ
    // （Finish ボタンで明示的に進行）
    return false;
  },

  onEnd: ({ G }: { G: SeattleGameState }) => {
    debug.log(`[goodsGrowthSeattlePhase] Phase 9 ended`);

    // クリーンアップ
    G.pendingGoodsPlacement = undefined;

    // フラグリセット（Issue #15対策）
    if ((G as any).refillGoodsStarted) {
      (G as any).refillGoodsStarted = false;
      debug.log('[goodsGrowthSeattlePhase] refillGoodsStarted flag reset');
    }

    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('refillGoods');
  },

  next: 'advanceTurn'
};
